import styled from 'styled-components';
import Image from 'next/image';
import { useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ProductFavorite } from '@components/products';
import { ArrowFilterIcon, CloseIcon } from '@components/icons';

const MobileProductSlider = ({ isZoomed, toggleZoom, product }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider({
        initial: currentSlide,
        slidesPerView: 1,
        centered: true,
        loop: true,
        spacing: 10,

        drag: !isZoomed,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });

    return (
        <Wrapper>
            {isZoomed && (
                <CloseButton onClick={toggleZoom}>
                    <CloseIcon />
                </CloseButton>
            )}
            <CarouselContainer isClicked={isZoomed}>
                <CarouselWrapper onClick={toggleZoom} isClicked={isZoomed}>
                    <HeroPhotoWrapper
                        ref={sliderRef}
                        className="keen-slider"
                        isClicked={isZoomed}
                    >
                        {product.images?.map((image, idx) => (
                            <NextImageWrapper
                                key={idx} // eslint-disable-line
                                className="keen-slider__slide"
                                isClicked={isZoomed}
                            >
                                {isZoomed ? (
                                    <TransformWrapper
                                        pinch={{
                                            step: 10,
                                        }}
                                        panning={{
                                            paddingSize: 0,
                                        }}
                                        alignmentAnimation={{
                                            sizeX: 0,
                                            sizeY: 0,
                                        }}
                                    >
                                        <TransformComponent
                                            wrapperStyle={{
                                                minHeight: '100vh',
                                                position: 'fixed',
                                                width: '100%',
                                                top: '0',
                                            }}
                                            contentStyle={{
                                                minHeight: '100vh',
                                                width: '100vw',
                                                margin: '0 auto',
                                            }}
                                        >
                                            <img
                                                src={image?.urls?.original}
                                                alt={product.name?.en}
                                                style={{
                                                    minHeight: '100vh',
                                                }}
                                            />
                                            {loaded && instanceRef.current && (
                                                <div>
                                                    <ArrowFilterIcon
                                                        onClick={(e) =>
                                                            e.stopPropagation() ||
                                                            instanceRef.current.prev()
                                                        }
                                                        disabled={
                                                            currentSlide === 0
                                                        }
                                                        style={{
                                                            width: '18px',
                                                            height: '30px',
                                                            position:
                                                                'absolute',
                                                            right: '95%',
                                                            top: '50%',
                                                            transform:
                                                                'rotate(180deg)',
                                                            opacity: '0.3',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                    <ArrowFilterIcon
                                                        onClick={(e) =>
                                                            e.stopPropagation() ||
                                                            instanceRef.current.next()
                                                        }
                                                        disabled={
                                                            currentSlide ===
                                                            instanceRef.current
                                                                .track.details
                                                                .slides.length -
                                                                1
                                                        }
                                                        style={{
                                                            width: '18px',
                                                            height: '30px',
                                                            position:
                                                                'absolute',
                                                            right: '4%',
                                                            top: '50%',
                                                            opacity: '0.3',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </TransformComponent>
                                    </TransformWrapper>
                                ) : (
                                    <div>
                                        <Image
                                            src={image?.urls?.original}
                                            fill
                                            alt={product.name?.en}
                                            priority={idx === 0}
                                            loading={
                                                idx === 0 ? 'eager' : 'lazy'
                                            }
                                            placeholder="blur"
                                            blurDataURL="/blurhash.png"
                                            sizes="(max-width: 768px) 100vw"
                                        />
                                    </div>
                                )}
                            </NextImageWrapper>
                        ))}
                    </HeroPhotoWrapper>
                    {loaded && instanceRef.current && (
                        <Dots className="dots">
                            {[
                                ...Array(
                                    instanceRef.current.track.details.slides
                                        .length,
                                ).keys(),
                            ].map((idx) => (
                                <Dot
                                    key={idx}
                                    onClick={() => {
                                        instanceRef.current?.moveToIdx(idx);
                                    }}
                                    isActive={currentSlide === idx}
                                    aria-label="Dots"
                                />
                            ))}
                        </Dots>
                    )}
                </CarouselWrapper>

                <ProductFavorite product={product} />
            </CarouselContainer>
        </Wrapper>
    );
};
const Wrapper = styled.div`
    @media (min-width: 1024px) {
        display: none;
    }
`;
const CarouselContainer = styled.div`
    position: relative;
    width: 100%;
    height: ${({ isClicked }) => (isClicked ? '100vh' : '165vw')};

    @media (max-width: 1024px) {
        z-index: ${({ isClicked }) => (isClicked ? 998 : 'unset')};
    }
`;
const CarouselWrapper = styled.div`
    display: flex;
    width: 100vw;
    height: 100%;
    position: relative;
    overflow: hidden;
    transition: all 0.5s ease-in-out;
    z-index: ${({ isClicked }) => isClicked && '998'};
`;

const CloseButton = styled.button`
    position: absolute;
    z-index: 999;
    top: 40px;
    right: 10px;
    background: none;
    border: none;
    outline: none;
    cursor: pointer;
    padding: 0;
    margin: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    transition: all 0.3s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        transform: rotate(90deg);
    }
`;

const HeroPhotoWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100vw;
    position: relative;
    transition: all 0.5s ease-in-out;
    height: ${({ isClicked }) => isClicked && '100vh'};

    div {
        height: fit-content;
    }

    @media (max-height: 600px) {
        height: calc(100vh - 125px);
    }
`;

const NextImageWrapper = styled.div`
    position: relative;
    width: 100%;
    overflow: hidden;
    height: ${({ isClicked }) => isClicked && '100vh'};
    transition: all 0.3s ease-in-out;
    min-height: 100vh;

    @supports (-webkit-touch-callout: none) {
        @media (max-height: 668px) {
            padding-top: 65px;
        }
        @media (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) {
            padding-top: 0;
        }
        @media (device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) {
            padding-top: 0;
        }
    }

    img {
        width: 100%;
        object-fit: cover;
        transition: all 0.5s ease-in-out;
        height: 100%;
    }
`;

const Dots = styled.div`
    z-index: 2;
    justify-content: flex-start;
    position: absolute;
    bottom: 18px;
    overflow: hidden;
    display: flex;
    width: 100%;
    padding: 20px 10px;
`;
const Dot = styled.button`
    border: none;
    width: ${({ isActive }) => (isActive ? '12px' : '12px')};
    height: ${({ isActive }) => (isActive ? '12px' : '12px')};
    min-width: 6px;
    padding: 0;
    border-radius: 100%;
    background: ${({ isActive }) => (isActive ? 'black' : '#c5c5c5')};
    margin: 0 5px;
    cursor: pointer;

    :focus {
        outline: none;
    }
`;

export default MobileProductSlider;
